import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { subscribeNewsletter } from "../api/redesignApi";
import { paths } from "../routing/paths";
import { trackEvent } from "../../analytics";

/*
 * Zone 5 — Support (Homepage reference): navy band, two dark cards.
 * Newsletter: real Mailchimp subscribe via the existing flow.
 * Chai Club: preset amounts + One-Time/Monthly toggle; the Donate CTA routes
 * to the existing donate flows (Stripe; PayPal remains an option there) —
 * per owner decision, no new payment backend in this project.
 */

const PRESETS = [18, 36, 54, 72, 180];

export default function SupportBand() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subState, setSubState] = useState("idle"); // idle | busy | done | error
  const [subMessage, setSubMessage] = useState("");
  const [amount, setAmount] = useState(36);
  const [cadence, setCadence] = useState("monthly"); // 'once' | 'monthly'

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value || !/.+@.+\..+/.test(value)) {
      setSubState("error");
      setSubMessage("Please enter a valid email address.");
      return;
    }
    setSubState("busy");
    try {
      await subscribeNewsletter(value);
      setSubState("done");
      setSubMessage("You're in! Look out for the next update.");
      setEmail("");
      try {
        trackEvent({ category: "support", action: "newsletter_signup" });
      } catch (_) {}
    } catch (err) {
      setSubState("error");
      setSubMessage(err?.message || "Something went wrong — please try again.");
    }
  };

  const donate = () => {
    try {
      trackEvent({ category: "support", action: "donate_click", label: cadence, value: amount });
    } catch (_) {}
    navigate(cadence === "monthly" ? `${paths.chaiClub()}?amount=${amount}` : paths.donations());
  };

  const presetClasses = (active) =>
    active
      ? "min-w-[64px] h-11 bg-ic-blue text-ic-offwhite font-sans text-[16px] font-bold border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
      : "min-w-[64px] h-11 bg-transparent text-ic-offwhite font-sans text-[16px] font-semibold border border-white/30 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark";

  return (
    <section className="bg-ic-navy mt-14 px-5 py-14 w-full">
      <div className="max-w-[1280px] mx-auto flex gap-6 flex-wrap items-stretch">
        {/* Newsletter */}
        <div className="bg-ic-navyCard border border-white/10 rounded-xl p-6 flex flex-col gap-3 shadow-ic-raised-dark" style={{ flex: "1 1 300px" }}>
          <h2 className="m-0 font-bold text-white tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            Never miss out on a new class again!
          </h2>
          <p className="m-0 text-[16px] leading-6 text-ic-lightGray">
            A short weekly email — what's new, and what's coming up on the calendar.
          </p>
          <form onSubmit={submit} className="flex gap-2 flex-wrap mt-1">
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (subState !== "idle") setSubState("idle");
              }}
              placeholder="you@example.com"
              aria-label="Email address"
              className="bg-transparent text-ic-offwhite placeholder:text-[#A3A3A3] font-sans text-[16px] font-normal leading-6 px-4 h-12 border border-ic-lightGray rounded-3xl box-border transition-all duration-150 ease-out focus:outline-none focus:border-ic-sky focus:shadow-ic-focus-dark-soft"
              style={{ flex: "1 1 200px" }}
            />
            <button
              type="submit"
              disabled={subState === "busy"}
              className="flex-shrink-0 bg-ic-blue text-ic-offwhite font-sans text-[16px] font-normal leading-6 px-8 h-12 border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive focus-visible:outline-none focus-visible:shadow-ic-focus disabled:opacity-70 disabled:cursor-wait"
            >
              {subState === "busy" ? "Signing up…" : "Sign up"}
            </button>
          </form>
          {subMessage && (
            <span className={`text-[14px] leading-5 ${subState === "error" ? "text-[#FF4242]" : "text-ic-lightGray"}`} role="status">
              {subMessage}
            </span>
          )}
        </div>

        {/* Chai Club */}
        <div className="bg-ic-navyCard border border-white/10 rounded-xl p-6 flex flex-col gap-3 shadow-ic-raised-dark" style={{ flex: "1 1 300px" }}>
          <h2 className="m-0 font-bold text-white tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
            Partner with the learning
          </h2>
          <p className="m-0 text-[16px] leading-6 text-ic-lightGray">
            Inside Chassidus is a nonprofit. The Chai Club keeps 15,000+ classes free — join monthly,
            or give once.
          </p>
          <div className="flex gap-2 flex-wrap" role="group" aria-label="Donation amount">
            {PRESETS.map((p) => (
              <button key={p} type="button" aria-pressed={amount === p} onClick={() => setAmount(p)} className={presetClasses(amount === p)}>
                ${p}
              </button>
            ))}
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <button
              type="button"
              aria-pressed={cadence === "once"}
              onClick={() => setCadence("once")}
              className={
                cadence === "once"
                  ? "h-11 px-5 bg-[rgba(24,108,206,0.2)] text-ic-sky font-sans text-[14px] font-bold border border-ic-sky rounded-3xl cursor-pointer focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                  : "h-11 px-5 bg-transparent text-ic-lightGray font-sans text-[14px] font-semibold border border-white/30 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              }
            >
              One-Time
            </button>
            <button
              type="button"
              aria-pressed={cadence === "monthly"}
              onClick={() => setCadence("monthly")}
              className={
                cadence === "monthly"
                  ? "h-11 px-5 bg-[rgba(24,108,206,0.2)] text-ic-sky font-sans text-[14px] font-bold border border-ic-sky rounded-3xl cursor-pointer focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
                  : "h-11 px-5 bg-transparent text-ic-lightGray font-sans text-[14px] font-semibold border border-white/30 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:border-ic-sky hover:text-ic-sky focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              }
            >
              Monthly · Chai Club
            </button>
            <button
              type="button"
              onClick={donate}
              className="ml-auto bg-ic-blue text-ic-offwhite font-sans text-[18px] font-bold leading-7 px-8 h-12 border-0 rounded-3xl cursor-pointer transition-all duration-150 ease-out hover:bg-ic-blueHover hover:shadow-ic-cta-hover active:bg-ic-blueActive active:shadow-ic-inset focus-visible:outline-none focus-visible:shadow-ic-focus"
            >
              Donate
            </button>
          </div>
        </div>
      </div>
      <div className="max-w-[1280px] mx-auto mt-10 flex justify-center border-t border-white/10 pt-6">
        <span className="text-[14px] leading-5 text-ic-lightGray text-center">
          A project of Inside Chassidus ·{" "}
          <a
            href="mailto:Info@insidechassidus.org"
            className="text-ic-lightGray underline hover:text-ic-sky inline-flex items-center py-3 -my-3 min-h-[44px]"
          >
            Info@insidechassidus.org
          </a>
        </span>
      </div>
    </section>
  );
}
